import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import { rand, monthLabel } from '@/components/stokvel/format';

const TYPE_LABEL = { contribution: 'Member deduction', company: 'Company contribution', penalty: 'Penalty fee', refund: 'Exit refund' };

const Row = ({ e, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [amount, setAmount] = useState(String(e.amount));
  const [note, setNote] = useState(e.note || '');
  const save = async () => {
    try { await axios.put(`${API}/stokvel/admin/ledger/${e.id}`, { amount: Number(amount), note }); setEdit(false); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${TYPE_LABEL[e.type]} for ${monthLabel(e.month)}?`)) return;
    try { await axios.delete(`${API}/stokvel/admin/ledger/${e.id}`); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <tr className={e.deleted ? 'opacity-40 line-through' : ''} data-testid={`stokvel-ledger-row-${e.id}`}>
      <td className="px-2 py-1.5">{monthLabel(e.month)}</td>
      <td className="px-2 py-1.5">{TYPE_LABEL[e.type]} <span className="text-[10px] text-stone-400">{e.source}</span></td>
      <td className="px-2 py-1.5 text-right font-mono">
        {edit ? <Input className="h-7 w-24 ml-auto" type="number" value={amount} onChange={x => setAmount(x.target.value)} data-testid={`stokvel-ledger-amount-${e.id}`} /> : rand(e.amount)}
      </td>
      <td className="px-2 py-1.5 text-xs text-stone-500">
        {edit ? <Input className="h-7" value={note} onChange={x => setNote(x.target.value)} placeholder="Reason" /> : e.note}
      </td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {e.deleted ? <span className="text-xs">deleted</span> : edit ? (
          <>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={save} data-testid={`stokvel-ledger-save-${e.id}`}><Check className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(false)}><X className="w-4 h-4" /></Button>
          </>
        ) : (
          <>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(true)} data-testid={`stokvel-ledger-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-700" onClick={del} data-testid={`stokvel-ledger-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </>
        )}
      </td>
    </tr>
  );
};

export const LedgerDialog = ({ item, onClose, onDone }) => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ month: '', type: 'contribution', amount: '', note: '' });
  const load = useCallback(async () => {
    if (!item) return;
    try { setRows((await axios.get(`${API}/stokvel/admin/ledger`, { params: { membership_id: item.id } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load ledger'); }
  }, [item]);
  useEffect(() => { load(); setForm({ month: '', type: 'contribution', amount: String(item?.monthly_amount ?? ''), note: '' }); }, [item, load]);
  const changed = () => { load(); onDone(); };
  const add = async () => {
    try {
      await axios.post(`${API}/stokvel/admin/ledger`, { membership_id: item.id, ...form, amount: Number(form.amount) });
      toast.success('Entry added'); setForm({ ...form, month: '', note: '' }); changed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };
  return (
    <Dialog open={!!item} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stokvel-ledger-dialog">
        <DialogHeader><DialogTitle>Ledger — {item?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-stone-500">Deductions and company contributions are created automatically on each payday (25th). Edit or delete any month, or add months for late opt-ins and corrections. Deleted automatic months are not recreated.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end bg-stone-50 p-2 rounded">
          <div><Label className="text-xs">Month</Label><Input type="month" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })} data-testid="stokvel-ledger-add-month" /></div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="stokvel-ledger-add-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Amount (R)</Label><Input type="number" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="stokvel-ledger-add-amount" /></div>
          <div><Label className="text-xs">Note</Label><Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} data-testid="stokvel-ledger-add-note" /></div>
          <Button onClick={add} disabled={!form.month || form.amount === ''} className="bg-stone-800 text-white" data-testid="stokvel-ledger-add-btn">Add</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-xs uppercase text-stone-600"><tr><th className="text-left px-2 py-1.5">Month</th><th className="text-left px-2 py-1.5">Type</th><th className="text-right px-2 py-1.5">Amount</th><th className="text-left px-2 py-1.5">Note</th><th></th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map(e => <Row key={e.id} e={e} onChanged={changed} />)}
            {!rows.length && <tr><td colSpan={5} className="text-center text-stone-400 py-4">No entries yet.</td></tr>}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
};
